import React, { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, LocateFixed, MapPin, X } from "lucide-react";

// Shared locations in one-to-one and group chat. Maps are OpenStreetMap (no API key): static tile images in the
// message bubble, and an interactive Leaflet map in the share popup and the location viewer.
const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';
const TILE_SIZE = 256;
const MAP_ZOOM = 16;
const MAX_ZOOM = 19;
// Half the bubble map's largest size, so its tiles always cover it
const STATIC_COVER_HALF_WIDTH = 140;
const STATIC_COVER_HALF_HEIGHT = 80;

const LOCATION_ERRORS = {
  1: "Location access is blocked. Allow it in your browser settings to share your location.",
  2: "Your location is not available right now.",
  3: "Finding your location took too long. Please try again.",
};

const PIN_ICON_HTML =
  '<svg viewBox="0 0 24 24" width="36" height="36" fill="#E5484D" stroke="#fff" stroke-width="1.5"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#fff" stroke="none"/></svg>';

export const mapsLinkFor = (latitude, longitude) =>
  `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

// Web Mercator pixel position of a point on the world map at MAP_ZOOM
const worldPixel = (latitude, longitude) => {
  const scale = TILE_SIZE * 2 ** MAP_ZOOM;
  const sin = Math.sin((latitude * Math.PI) / 180);
  return {
    x: ((longitude + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
};

// A few tile images around a pin: light enough for every location message in a conversation
const StaticMap = ({ latitude, longitude, className }) => {
  const { x, y } = worldPixel(latitude, longitude);
  const tileCount = 2 ** MAP_ZOOM;
  const tiles = [];
  for (let ty = Math.floor((y - STATIC_COVER_HALF_HEIGHT) / TILE_SIZE); ty <= Math.floor((y + STATIC_COVER_HALF_HEIGHT) / TILE_SIZE); ty += 1) {
    for (let tx = Math.floor((x - STATIC_COVER_HALF_WIDTH) / TILE_SIZE); tx <= Math.floor((x + STATIC_COVER_HALF_WIDTH) / TILE_SIZE); tx += 1) {
      if (ty >= 0 && ty < tileCount) tiles.push({ tx, ty });
    }
  }
  return (
    <span className={`relative block overflow-hidden bg-[#E5E3DF] ${className}`}>
      {tiles.map(({ tx, ty }) => (
        <img
          key={`${tx}_${ty}`}
          src={`https://tile.openstreetmap.org/${MAP_ZOOM}/${((tx % tileCount) + tileCount) % tileCount}/${ty}.png`}
          alt=""
          loading="lazy"
          draggable={false}
          className="absolute max-w-none"
          style={{
            width: TILE_SIZE,
            height: TILE_SIZE,
            left: `calc(50% + ${tx * TILE_SIZE - x}px)`,
            top: `calc(50% + ${ty * TILE_SIZE - y}px)`,
          }}
        />
      ))}
      <MapPin className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full w-8 h-8 text-[#E5484D] fill-white drop-shadow" />
      <span className="absolute bottom-0 right-0 bg-white/80 px-1 text-[9px] leading-4 text-gray-600">
        © OpenStreetMap contributors
      </span>
    </span>
  );
};

// Leaflet and its CSS load only when a map is opened
const loadLeaflet = () =>
  Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]).then(([module]) => module.default || module);

// Interactive map in `containerRef`, created once `center` is known. With `zoomAroundCenter`, every way of
// zooming keeps the map centre in place (the share popup's pin is the centre).
const useLeafletMap = (containerRef, center, { zoomAroundCenter = false } = {}) => {
  const [leaflet, setLeaflet] = useState(null);
  const [failed, setFailed] = useState(false);
  const centerRef = useRef(center);
  centerRef.current = center;
  const hasCenter = !!center;

  useEffect(() => {
    if (!hasCenter) return undefined;
    let map = null;
    let cancelled = false;
    const zoomMode = zoomAroundCenter ? "center" : true;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current) return;
        map = L.map(containerRef.current, {
          maxZoom: MAX_ZOOM,
          scrollWheelZoom: zoomMode,
          touchZoom: zoomMode,
          doubleClickZoom: zoomMode,
        }).setView(centerRef.current, MAP_ZOOM);
        map.attributionControl.setPrefix(false);
        L.tileLayer(TILE_URL, { maxZoom: MAX_ZOOM, attribution: OSM_ATTRIBUTION }).addTo(map);
        setLeaflet({ L, map });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (map) map.remove();
    };
  }, [hasCenter, zoomAroundCenter, containerRef]);

  return { leaflet, failed };
};

const MapModal = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <h3 className="font-semibold text-[#1F1F1F] truncate">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 shrink-0" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const MapButton = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute right-3 bottom-8 z-[500] w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-[#095228] hover:bg-gray-50"
    aria-label={label}
    title={label}
  >
    {children}
  </button>
);

// The bubble of a location message; opens the location viewer
export const LocationCard = ({ item, onOpen }) => {
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  const label = (
    <span className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-[#095228]">
      <MapPin className="w-4 h-4 shrink-0" />
      <span className="truncate">{item.address || "Location"}</span>
    </span>
  );
  const cardClass = "block w-[260px] max-w-full overflow-hidden rounded-lg border border-black/10 bg-white text-left";

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer" className={cardClass}>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(item)} className={cardClass}>
      <StaticMap latitude={latitude} longitude={longitude} className="w-full h-36" />
      {label}
    </button>
  );
};

// A shared location on a map the viewer can move and zoom, with a link to Google Maps for directions
export const LocationViewer = ({ item, onClose }) => {
  const containerRef = useRef(null);
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  const { leaflet, failed } = useLeafletMap(containerRef, [latitude, longitude]);

  useEffect(() => {
    if (!leaflet) return undefined;
    const { L, map } = leaflet;
    const marker = L.marker([latitude, longitude], {
      icon: L.divIcon({ html: PIN_ICON_HTML, className: "", iconSize: [36, 36], iconAnchor: [18, 34] }),
      interactive: false,
    }).addTo(map);
    return () => marker.remove();
  }, [leaflet, latitude, longitude]);

  return (
    <MapModal title={item.address || "Location"} onClose={onClose}>
      <div className="relative h-80 bg-gray-100">
        <div ref={containerRef} className="absolute inset-0" />
        {!leaflet && (
          <p className={`absolute inset-0 flex items-center justify-center text-sm ${failed ? "text-red-500" : "text-gray-500"}`}>
            {failed ? "The map could not be loaded." : "Loading map..."}
          </p>
        )}
        {leaflet && (
          <MapButton
            onClick={() => leaflet.map.setView([latitude, longitude], Math.max(leaflet.map.getZoom(), MAP_ZOOM))}
            label="Back to the shared location"
          >
            <LocateFixed className="w-5 h-5" />
          </MapButton>
        )}
      </div>
      <div className="p-4">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#095228] text-[#095228] text-sm font-semibold hover:bg-[#EBF8ED]"
        >
          <ExternalLink className="w-4 h-4" /> Open in Google Maps
        </a>
      </div>
    </MapModal>
  );
};

// Follows the device position while open (the first fix is often coarse, later ones refine it). The blue dot and
// circle show the position and its accuracy; dragging the map places a pin at the centre to send another place
// instead, and the locate button goes back to the device position with a fresh fix.
export const ShareLocationModal = ({ onSend, onClose }) => {
  const containerRef = useRef(null);
  const watchRef = useRef(null);
  const followRef = useRef(true);
  const hasPositionRef = useRef(false);
  const layersRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [error, setError] = useState("");
  const [locating, setLocating] = useState(true);
  const [moved, setMoved] = useState(false);
  const { leaflet, failed } = useLeafletMap(containerRef, position && [position.latitude, position.longitude], {
    zoomAroundCenter: true,
  });

  const locate = useCallback(() => {
    setError("");
    if (!navigator.geolocation) {
      setError("Your browser cannot share a location.");
      setLocating(false);
      return;
    }
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    setLocating(true);
    watchRef.current = navigator.geolocation.watchPosition(
      ({ coords }) => {
        hasPositionRef.current = true;
        setLocating(false);
        setError("");
        setPosition({ latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy });
      },
      (err) => {
        setLocating(false);
        // A later fix can still arrive, so only a blocked permission replaces a position already found
        if (!hasPositionRef.current || err.code === 1) {
          setError(LOCATION_ERRORS[err.code] || "Could not find your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, []);

  useEffect(() => {
    locate();
    return () => {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    };
  }, [locate]);

  useEffect(() => {
    if (!leaflet) return undefined;
    const onDrag = () => {
      followRef.current = false;
      setMoved(true);
    };
    leaflet.map.on("dragstart", onDrag);
    return () => leaflet.map.off("dragstart", onDrag);
  }, [leaflet]);

  useEffect(() => {
    if (!leaflet || !position) return;
    const { L, map } = leaflet;
    const latLng = [position.latitude, position.longitude];
    if (!layersRef.current) {
      layersRef.current = {
        accuracy: L.circle(latLng, {
          radius: position.accuracy,
          color: "#08AEEA",
          weight: 1,
          fillOpacity: 0.12,
          interactive: false,
        }).addTo(map),
        dot: L.circleMarker(latLng, {
          radius: 7,
          color: "#fff",
          weight: 2,
          fillColor: "#08AEEA",
          fillOpacity: 1,
          interactive: false,
        }).addTo(map),
      };
    } else {
      layersRef.current.accuracy.setLatLng(latLng).setRadius(position.accuracy);
      layersRef.current.dot.setLatLng(latLng);
    }
    if (followRef.current) map.panTo(latLng);
  }, [leaflet, position]);

  const goToMyLocation = () => {
    followRef.current = true;
    setMoved(false);
    if (leaflet && position) {
      leaflet.map.setView([position.latitude, position.longitude], Math.max(leaflet.map.getZoom(), MAP_ZOOM));
    }
    locate();
  };

  const send = () => {
    if (!moved) {
      onSend(position);
      return;
    }
    const center = leaflet.map.getCenter().wrap();
    onSend({ latitude: center.lat, longitude: center.lng });
  };

  const status = failed
    ? "The map could not be loaded."
    : error && !position
      ? error
      : position
        ? "Loading map..."
        : "Finding your location...";

  return (
    <MapModal title="Share location" onClose={onClose}>
      <div className="relative h-80 bg-gray-100">
        <div ref={containerRef} className="absolute inset-0" />
        {!leaflet && (
          <p
            className={`absolute inset-0 flex items-center justify-center px-6 text-center text-sm ${
              failed || (error && !position) ? "text-red-500" : "text-gray-500"
            }`}
          >
            {status}
          </p>
        )}
        {leaflet && moved && (
          <MapPin className="pointer-events-none absolute left-1/2 top-1/2 z-[500] -translate-x-1/2 -translate-y-full w-9 h-9 text-[#E5484D] fill-white drop-shadow" />
        )}
        {leaflet && (
          <MapButton onClick={goToMyLocation} label="Go to my location">
            <LocateFixed className={`w-5 h-5 ${locating ? "animate-pulse" : ""}`} />
          </MapButton>
        )}
      </div>
      <div className="p-4 space-y-3">
        {error && position && <p className="text-xs text-red-500">{error}</p>}
        {moved ? (
          <p className="text-xs text-gray-500">Drag the map to place the pin, or tap the locate button to go back.</p>
        ) : (
          position && (
            <p className="text-xs text-gray-500">
              {locating ? "Updating your location... " : ""}Accurate to {Math.round(position.accuracy)} m
            </p>
          )
        )}
        {error && !position ? (
          <button
            onClick={locate}
            className="w-full py-2.5 rounded-xl border border-[#095228] text-[#095228] text-sm font-semibold"
          >
            Try again
          </button>
        ) : (
          <button
            onClick={send}
            disabled={!leaflet || !position}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#1DE78B] to-[#08AEEA] hover:opacity-90 disabled:opacity-50"
          >
            <MapPin className="w-4 h-4" /> {moved ? "Send this location" : "Send your current location"}
          </button>
        )}
      </div>
    </MapModal>
  );
};
